import React, { useEffect, useRef, useState, useMemo } from "react";
import * as echarts from "echarts";

// Helper to get FY from date
const getFinancialYear = (dateStr) => {
  const date = new Date(dateStr);
  const year = date.getFullYear();
  const month = date.getMonth();
  const fyStart = month >= 3 ? year : year - 1;
  return `FY ${fyStart}-${fyStart + 1}`;
};

// Extract unique FYs from projects
const getFinancialYears = (projects, includePreviousFY = false) => {
  const fySet = new Set();
  let minFYYear = new Date().getFullYear();

  projects.forEach((p) => {
    const date = new Date(p.start_date);
    const year = date.getFullYear();
    const month = date.getMonth();
    const fyStart = month >= 3 ? year : year - 1;
    const fyLabel = `FY ${fyStart}-${fyStart + 1}`;

    fySet.add(fyLabel);
    if (fyStart < minFYYear) {
      minFYYear = fyStart;
    }
  });

  if (includePreviousFY) {
    fySet.add(`FY ${minFYYear - 1}-${minFYYear}`);
  }

  return [...fySet].sort((a, b) => {
    const aStart = +a.split(" ")[1].split("-")[0];
    const bStart = +b.split(" ")[1].split("-")[0];
    return bStart - aStart;
  });
};

const ProjectTimelineChart = ({ projects }) => {
  const chartRef = useRef(null);

  const financialYears = useMemo(() => getFinancialYears(projects), [projects]);
  const currentFY = getFinancialYear(new Date());
  const [selectedFY, setSelectedFY] = useState(currentFY);

  // Filter projects by selected FY
  const filteredProjects = useMemo(() => {
    if (!selectedFY) return projects;
    return projects.filter(
      (p) => getFinancialYear(p.start_date) === selectedFY
    );
  }, [projects, selectedFY]);

  useEffect(() => {
    if (!chartRef.current || filteredProjects.length === 0) return;

    const startTimeline = {};
    const endTimeline = {};

    filteredProjects.forEach((project) => {
      if (!project.start_date || !project.end_date) return;

      const startMonth = new Date(project.start_date).toLocaleString(
        "default",
        {
          month: "short",
          year: "numeric",
        }
      );
      const endMonth = new Date(project.end_date).toLocaleString("default", {
        month: "short",
        year: "numeric",
      });

      startTimeline[startMonth] = (startTimeline[startMonth] || 0) + 1;
      endTimeline[endMonth] = (endTimeline[endMonth] || 0) + 1;
    });

    const monthOrder = [
      "Jan",
      "Feb",
      "Mar",
      "Apr",
      "May",
      "Jun",
      "Jul",
      "Aug",
      "Sep",
      "Oct",
      "Nov",
      "Dec",
    ];

    const allMonths = [
      ...new Set([...Object.keys(startTimeline), ...Object.keys(endTimeline)]),
    ].sort((a, b) => {
      const [aMonth, aYear] = a.split(" ");
      const [bMonth, bYear] = b.split(" ");
      return (
        parseInt(aYear) - parseInt(bYear) ||
        monthOrder.indexOf(aMonth) - monthOrder.indexOf(bMonth)
      );
    });

    const startData = allMonths.map((month) => startTimeline[month] || 0);
    const endData = allMonths.map((month) => endTimeline[month] || 0);

    const chart = echarts.init(chartRef.current);
    const option = {
      tooltip: {
        trigger: "axis",
        axisPointer: { type: "line", axis: "x" },
      },
      legend: { data: ["Start Dates", "End Dates"] },
      grid: {
        left: "5%",
        right: "5%",
        bottom: "10%",
        top: "10%",
        containLabel: true,
      },
      xAxis: [{ type: "category", boundaryGap: false, data: allMonths }],
      yAxis: [{ type: "value" }],
      series: [
        {
          name: "Start Dates",
          type: "line",
          stack: "Total",
          areaStyle: {},
          emphasis: { focus: "series" },
          data: startData,
        },
        {
          name: "End Dates",
          type: "line",
          stack: "Total",
          areaStyle: {},
          emphasis: { focus: "series" },
          data: endData,
        },
      ],
    };

    chart.setOption(option);

    return () => {
      chart.dispose();
    };
  }, [filteredProjects]);

  return (
    <div style={{ width: "100%", maxWidth: "1200px", margin: "auto" }}>
      <h2 className="pie-header" style={{ top: "-16px" }}>
        Project Timeline Summary
      </h2>
      <select
        className="pm-fy-dd"
        value={selectedFY}
        onChange={(e) => setSelectedFY(e.target.value)}
      >
        <option value="">All Years</option>
        {financialYears.map((fy) => (
          <option key={fy} value={fy}>
            {fy}
          </option>
        ))}
      </select>
      <p className="bar-chart-text">Number of Projects</p>
      <p className="bar-chart-text-bottom">Project Timeline</p>
      <div
        ref={chartRef}
        style={{ width: "100%", height: "400px", minWidth: "800px" }}
      />
    </div>
  );
};

export default ProjectTimelineChart;
